import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRightLeft, LogIn } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { completeSignInNavigation } from '@/lib/sessionReset';

function authErrorMessage(err: unknown, t: (k: string) => string): string {
  const code = (err as { code?: string }).code;
  if (code === 'auth/email-already-in-use') return t('auth.emailInUse');
  if (code === 'auth/invalid-credential' || code === 'auth/wrong-password' || code === 'auth/user-not-found') {
    return t('auth.invalidCredential');
  }
  if (code === 'auth/too-many-requests') return t('auth.tooManyRequests');
  if (code === 'auth/invalid-email') return t('auth.invalidEmail');
  return t('auth.failed');
}

function accountInitial(email: string): string {
  const local = email.trim().split('@')[0] ?? '';
  return (local.charAt(0) || '?').toUpperCase();
}

export function AuthPage() {
  const { t } = useTranslation();
  const { user, loading, signIn, signUp, mfaResolver, completeMfaSignIn, switchAccount } = useAuth();
  const [mfaCode, setMfaCode] = useState('');
  const online = useOnlineStatus();
  const toast = useToast();
  const [mode, setMode] = useState<'in' | 'up'>('in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!online) return;
    setBusy(true);
    try {
      if (mfaResolver) {
        await completeMfaSignIn(mfaCode);
        return;
      }
      if (mode === 'in') await signIn(email, password);
      else await signUp(email, password);
    } catch (err) {
      const code = (err as { code?: string }).code;
      if (code === 'auth/email-already-in-use') setMode('in');
      toast.error(authErrorMessage(err, t), 'auth');
    } finally {
      setBusy(false);
    }
  }

  if (!loading && user && !mfaResolver) {
    const accountEmail = user.email ?? user.uid;

    return (
      <div className="flex min-h-dvh items-center justify-center bg-[var(--fv-bg)] p-4">
        <div className="w-full max-w-md rounded-[var(--fv-radius-lg)] border border-[var(--fv-border)] bg-[var(--fv-surface)] p-8 shadow-sm">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-[var(--fv-primary)]">{t('app.name')}</h1>
            <p className="mt-1 text-sm text-[var(--fv-text-muted)]">{t('auth.sessionResume')}</p>
          </div>

          <div className="mt-8 rounded-[var(--fv-radius-lg)] border border-[var(--fv-border)] bg-[var(--fv-bg)] px-5 py-6 text-center">
            <div
              className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[var(--fv-primary-muted)] text-lg font-bold text-[var(--fv-primary)]"
              aria-hidden
            >
              {accountInitial(accountEmail)}
            </div>
            <p className="mt-4 text-xs font-medium uppercase tracking-wide text-[var(--fv-text-muted)]">
              {t('auth.signedInLabel')}
            </p>
            <p className="mt-1 truncate text-base font-semibold text-[var(--fv-text)]" title={accountEmail}>
              {accountEmail}
            </p>
          </div>

          <div className="mt-6 flex flex-col gap-2">
            <Button type="button" fullWidth onClick={() => void completeSignInNavigation()}>
              <LogIn className="h-4 w-4 shrink-0" aria-hidden />
              {t('auth.continue')}
            </Button>
            <Button type="button" variant="secondary" fullWidth onClick={() => void switchAccount()}>
              <ArrowRightLeft className="h-4 w-4 shrink-0" aria-hidden />
              {t('auth.switchAccount')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-[var(--fv-bg)] p-4">
      <div className="w-full max-w-md rounded-[var(--fv-radius-lg)] border border-[var(--fv-border)] bg-[var(--fv-surface)] p-8 shadow-sm">
        <h1 className="text-2xl font-bold text-[var(--fv-primary)]">{t('app.name')}</h1>
        <p className="mt-1 text-sm text-[var(--fv-text-muted)]">{t('app.tagline')}</p>
        <form className="mt-8 space-y-4" onSubmit={(e) => void handleSubmit(e)}>
          {mfaResolver ? (
            <>
              <p className="text-sm text-[var(--fv-text-muted)]">{t('auth.mfaPrompt')}</p>
              <label className="block text-sm font-medium">
                {t('auth.mfaCode')}
                <input
                  className="mt-1 w-full rounded border px-3 py-2"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  autoFocus
                  required
                />
              </label>
            </>
          ) : (
            <>
              <label className="block text-sm font-medium">
                {t('auth.email')}
                <input
                  type="email"
                  required
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="mt-1 w-full rounded-[var(--fv-radius)] border border-[var(--fv-border)] px-3 py-2"
                  disabled={!online || busy}
                />
              </label>
              <label className="block text-sm font-medium">
                {t('auth.password')}
                <input
                  type="password"
                  required
                  minLength={6}
                  autoComplete={mode === 'in' ? 'current-password' : 'new-password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-1 w-full rounded-[var(--fv-radius)] border border-[var(--fv-border)] px-3 py-2"
                  disabled={!online || busy}
                />
              </label>
            </>
          )}
          <Button type="submit" fullWidth loading={busy} disabled={!online}>
            {mfaResolver ? t('auth.verifyMfa') : mode === 'in' ? t('auth.signIn') : t('auth.signUp')}
          </Button>
        </form>
        {!mfaResolver && (
          <Button
            type="button"
            variant="link"
            fullWidth
            className="mt-4 justify-center"
            onClick={() => setMode(mode === 'in' ? 'up' : 'in')}
          >
            {mode === 'in' ? t('auth.noAccount') : t('auth.hasAccount')}
          </Button>
        )}
      </div>
    </div>
  );
}
